import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Shield, Palette, Check } from 'lucide-react';
import { clearToken, getCurrentUser, canManageUsers } from '../../lib/auth';
import { getCurrentCompany, getLOGO_URL, THEMES, applyTheme, getStoredTheme } from '../../lib/brandConfig';
import UsersTab from '../users/UsersTab';
import CompanyProfileCard from './CompanyProfileCard';

// ── Theme Picker Card ─────────────────────────────────────────────────────────
function ThemePickerCard() {
  const [activeTheme, setActiveTheme] = useState(getStoredTheme);

  const handleSelect = (key) => {
    applyTheme(key);
    setActiveTheme(key);
  };

  useEffect(() => {
    const handler = (e) => setActiveTheme(e.detail.theme);
    window.addEventListener('theme-changed', handler);
    return () => window.removeEventListener('theme-changed', handler);
  }, []);

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{ background: 'var(--theme-card-bg)', border: '1px solid var(--theme-border)' }}
    >
      <div
        className="px-6 py-4 flex items-center gap-2"
        style={{ borderBottom: '1px solid var(--theme-border)' }}
      >
        <Palette className="w-4 h-4" style={{ color: 'var(--theme-accent)' }} />
        <h2
          className="text-base font-semibold"
          style={{ fontFamily: "'Space Grotesk', sans-serif", color: 'var(--theme-text-primary)' }}
        >
          UI Theme
        </h2>
      </div>

      <div className="px-6 py-4 flex gap-4 flex-wrap">
        {Object.values(THEMES).map((theme) => {
          const isActive = activeTheme === theme.key;
          return (
            <button
              key={theme.key}
              onClick={() => handleSelect(theme.key)}
              className="flex flex-col items-center gap-2 rounded-lg transition-all"
              style={{
                padding: '12px 16px',
                border: isActive
                  ? `2px solid var(--theme-accent)`
                  : '2px solid var(--theme-border)',
                background: isActive ? 'var(--theme-accent-sub)' : 'transparent',
                cursor: 'pointer',
                minWidth: 100,
              }}
            >
              {/* Mini preview */}
              <div
                className="rounded-md overflow-hidden"
                style={{ width: 72, height: 52, boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}
              >
                {/* Header strip */}
                <div style={{ height: 18, background: theme.preview.header }} />
                {/* Body */}
                <div style={{ height: 34, background: theme.preview.bg, padding: '4px 6px' }}>
                  <div
                    style={{
                      height: 4,
                      borderRadius: 2,
                      background: theme.preview.accent,
                      width: '60%',
                      marginBottom: 4,
                    }}
                  />
                  <div
                    style={{
                      height: 4,
                      borderRadius: 2,
                      background: theme.preview.header,
                      opacity: 0.25,
                      width: '80%',
                    }}
                  />
                </div>
              </div>

              {/* Label */}
              <span
                className="text-xs font-semibold flex items-center gap-1"
                style={{ color: isActive ? 'var(--theme-accent)' : 'var(--theme-text-muted)' }}
              >
                {isActive && <Check className="w-3 h-3" />}
                {theme.name}
                <span style={{ opacity: 0.55 }}>· {theme.label}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="px-6 pb-4">
        <p className="text-xs" style={{ color: 'var(--theme-text-light)' }}>
          Theme is saved locally on this device. Company admins can set the default for their account.
        </p>
      </div>
    </div>
  );
}

export default function SettingsTab() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(() => getCurrentCompany() || {});
  const [logoUrl, setLogoUrl] = useState(() => getLOGO_URL() || '/LOGO.jpg');
  const user = getCurrentUser();

  // Listen for brand updates from CompanyProfileCard
  const refreshBrand = useCallback(() => {
    setCompany(getCurrentCompany() || {});
    setLogoUrl(getLOGO_URL() || '/LOGO.jpg');
  }, []);

  useEffect(() => {
    const handler = () => refreshBrand();
    window.addEventListener('company-brand-updated', handler);
    return () => window.removeEventListener('company-brand-updated', handler);
  }, [refreshBrand]);

  const handleLogout = () => {
    clearToken();
    navigate('/login', { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* Profile + Logout Card */}
      <div
        className="rounded-lg overflow-hidden"
        style={{ background: 'var(--theme-card-bg)', border: '1px solid var(--theme-border)' }}
      >
        {/* Card Header: LOGO + User Info + Log Out */}
        <div
          className="px-6 py-4 flex items-center justify-between"
          style={{ borderBottom: '1px solid var(--theme-border)' }}
        >
          {/* Left: Company LOGO + User avatar + name + company */}
          <div className="flex items-center gap-3">
            <img
              src={logoUrl}
              alt={company?.name || 'Company'}
              style={{ maxWidth: 48, maxHeight: 48, objectFit: 'contain' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'var(--theme-header-bg)' }}
            >
              <User className="w-5 h-5" style={{ color: 'var(--theme-card-bg)' }} />
            </div>
            <div>
              <h3
                className="text-sm font-semibold"
                style={{ fontFamily: "'Space Grotesk', sans-serif", color: 'var(--theme-text-primary)' }}
              >
                {user?.full_name || user?.username || 'User'}
              </h3>
              <p className="text-xs" style={{ color: 'var(--theme-text-muted)' }}>
                {company?.name || 'Revolead Construction'}
                {' '}&middot;{' '}
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                  style={{
                    background: ['system_admin','company_admin','admin'].includes(user?.role)
                      ? 'rgba(201,168,76,0.15)'
                      : user?.role === 'project_admin'
                      ? 'rgba(59,130,246,0.12)'
                      : 'rgba(107,114,128,0.1)',
                    color: ['system_admin','company_admin','admin'].includes(user?.role)
                      ? '#c9a84c'
                      : user?.role === 'project_admin'
                      ? '#1e40af'
                      : '#6b7280',
                  }}
                >
                  {user?.role || 'estimator'}
                </span>
              </p>
            </div>
          </div>

          {/* Right: Log Out */}
          <div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors"
              style={{ background: '#dc2626', color: '#fff' }}
              onMouseEnter={(e) => { e.target.style.background = '#b91c1c'; }}
              onMouseLeave={(e) => { e.target.style.background = '#dc2626'; }}
            >
              <LogOut className="w-4 h-4" />
              Log Out
            </button>
          </div>
        </div>

        {/* Account Info */}
        <div className="px-6 py-3 grid grid-cols-2 gap-x-8 gap-y-2">
          <div>
            <span className="text-xs text-[#9ca3af]">Username</span>
            <p className="text-sm text-[#2c2c2c]">{user?.username || '—'}</p>
          </div>
          <div>
            <span className="text-xs text-[#9ca3af]">Email</span>
            <p className="text-sm text-[#2c2c2c]">{user?.email || '—'}</p>
          </div>
          <div>
            <span className="text-xs text-[#9ca3af]">Company</span>
            <p className="text-sm text-[#2c2c2c]">{company?.name || '—'}</p>
          </div>
          <div>
            <span className="text-xs text-[#9ca3af]">ACN</span>
            <p className="text-sm text-[#2c2c2c]">{company?.acn || '—'}</p>
          </div>
        </div>
      </div>

      {/* Company Profile (admin only) */}
      <CompanyProfileCard />

      {/* Theme Picker — company_admin and system_admin */}
      {canManageUsers() && <ThemePickerCard />}

      {/* User Management — only company_admin and system_admin */}
      {canManageUsers() && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-[#c9a84c]" />
            <h2
              className="text-base font-semibold text-[#1a1a2e]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              User Management
            </h2>
          </div>
          <UsersTab />
        </div>
      )}
    </div>
  );
}
